import React from 'react';
import { AudioConfig } from '../../LiveContext/AudioConfigContext/Hooks/useAudioConfig';

export default function useAudioStream(enabled: boolean, audioConfig: AudioConfig) {
  const [streamRef, setStreamRef] = React.useState<MediaStream | null>(null);
  const deviceId = React.useMemo(() => audioConfig.deviceId, [audioConfig.deviceId]);
  const audioConfigRef = React.useRef(audioConfig);

  React.useEffect(() => {
    audioConfigRef.current = audioConfig;
  }, [audioConfig]);

  React.useEffect(() => {
    let isMounted = true;
    let localStream: MediaStream | null = null;

    if (enabled) {
      if (!deviceId) {
        throw new Error('No audio device selected, cannot start audio stream');
      }
      navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          sampleRate: { ideal: 16000 },
          noiseSuppression: audioConfigRef.current.noiseSuppression,
          echoCancellation: audioConfigRef.current.echoCancellation,
          autoGainControl: audioConfigRef.current.autoGainControl,
        },
      }).then((stream) => {
        localStream = stream;
        if (isMounted) {
          setStreamRef(stream);
        } else {
          console.warn('Stopping stream because component unmounted before stream ready');
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          setStreamRef(null);
        }
      }).catch((error) => {
        // The device can disappear or be taken by another application between being
        // listed and being opened, and the user can revoke the grant mid-session.
        console.error('Unable to open the audio stream', error);
        if (isMounted) setStreamRef(null);
      });
    }
    return () => {
      isMounted = false;
      localStream?.getTracks().forEach((track) => {
        track.stop();
      });
      setStreamRef(null);
    };
  }, [enabled, deviceId]);

  return streamRef;
}
